import { ReactNode, useState } from 'react';
import { Card, MaterialSymbol, PaasTransaction, ProgressBar, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';
import type { EnrichedReports } from '@/hooks/useApi/useFetchEnrichedReports';
import type { EnrichmentProgress, EnrichmentStage, EnrichmentTier } from '../useDataEnrichment';
import type { CategoryDescriptor } from '../categories';
import InsightsPanel from '../InsightsPanel/InsightsPanel';
import styles from './DataEnrichmentContent.module.scss';

const cn = withStyles(styles);

type SegTab = 'details' | 'insights';

type DataEnrichmentContentProps = {
  stage: EnrichmentStage;
  tier: EnrichmentTier;
  progress: EnrichmentProgress;
  reports: EnrichedReports | null;
  transactions: PaasTransaction[];
  currency: string;
  categories: CategoryDescriptor[];
  // Bank account backed by a supported aggregator — tabs only show when true
  isInsightsAvailable: boolean;
  // Account-level feature flag; when off the Insights tab is locked
  isFeatureEnabled: boolean;
  // Starts the enrichment/load flow (or shows the locked-feature dialogue)
  onRequestInsights: () => void;
  onUpdateCategory: (transactionID: string, classification: string) => Promise<void>;
  // The pre-existing detail cards, shown under the Details tab
  detailsContent: ReactNode;
};

export default function DataEnrichmentContent({
  stage,
  tier,
  progress,
  reports,
  transactions,
  currency,
  categories,
  isInsightsAvailable,
  isFeatureEnabled,
  onRequestInsights,
  onUpdateCategory,
  detailsContent,
}: DataEnrichmentContentProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.DataEnrichment' });
  const [activeTab, setActiveTab] = useState<SegTab>('details');

  if (!isInsightsAvailable) {
    return <>{detailsContent}</>;
  }

  function handleInsightsTabClick() {
    if (!isFeatureEnabled) {
      // Locked feature: show the upsell dialogue and stay on Details
      onRequestInsights();

      return;
    }

    setActiveTab('insights');

    if (stage === 'locked') {
      onRequestInsights();
    }
  }

  function renderInsightsTab() {
    if (stage === 'ready' && reports) {
      return (
        <InsightsPanel
          reports={reports}
          transactions={transactions}
          tier={tier}
          currency={currency}
          categories={categories}
          onUpdateCategory={onUpdateCategory}
        />
      );
    }

    if (stage === 'enriching' || stage === 'loadingReports') {
      return (
        <Card className={cn('progressCard')} data-testid="enrichment-progress">
          <span className={cn('progressLabel')}>
            {stage === 'enriching'
              ? t('enrichingLabel', {
                  defaultValue: `Running full data enrichment${progress.numTotal > 0 ? ` on ${progress.numTotal} transactions` : ''}…`,
                  numTotal: progress.numTotal,
                })
              : t('loadingLabel', { defaultValue: 'Loading insights…' })}
          </span>

          {stage === 'enriching' && (
            <span className={cn('progressHint')}>
              {t('enrichingHint', {
                defaultValue:
                  'Each transaction is being classified and enriched — this can take a few minutes. You can keep working; progress updates automatically.',
              })}
            </span>
          )}

          {stage === 'enriching' && progress.numTotal > 0 && (
            <>
              <ProgressBar percentComplete={(progress.numEnriched / progress.numTotal) * 100} />

              <span className={cn('progressCount')}>
                {t('progressCount', {
                  defaultValue: `${progress.numEnriched} of ${progress.numTotal} transactions enriched (${Math.round(
                    (progress.numEnriched / progress.numTotal) * 100,
                  )}%)`,
                  numEnriched: progress.numEnriched,
                  numTotal: progress.numTotal,
                })}
              </span>
            </>
          )}
        </Card>
      );
    }

    if (stage === 'error') {
      return (
        <Card className={cn('errorCard')} data-testid="enrichment-error">
          <MaterialSymbol icon="warning" size="18px" color="#D22D2D" />
          {t('errorMessage', { defaultValue: 'Data enrichment failed. Please try again later.' })}
        </Card>
      );
    }

    // 'locked': the user opened the tab but dismissed the tier dialogue
    return (
      <Card className={cn('startCard')} data-testid="enrichment-start">
        <span className={cn('startText')}>
          {t('startPrompt', {
            defaultValue: 'Run data enrichment on this account to unlock income, spending and risk insights.',
          })}
        </span>

        <Button colorScheme="primary" onClick={onRequestInsights} data-testid="enrichment-start-button">
          {t('enrichData', { defaultValue: 'Enrich Data' })}
        </Button>
      </Card>
    );
  }

  return (
    <>
      <div className={cn('segTabs')} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'details'}
          className={cn('segTab', activeTab === 'details' && 'active')}
          onClick={() => setActiveTab('details')}
          data-testid="details-tab"
        >
          {t('detailsTab', { defaultValue: 'Details' })}
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'insights'}
          className={cn('segTab', activeTab === 'insights' && 'active')}
          onClick={handleInsightsTabClick}
          data-testid="insights-tab"
        >
          {!isFeatureEnabled && <MaterialSymbol icon="lock" size="14px" className={cn('tabLockIcon')} />}
          {t('insightsTab', { defaultValue: 'Insights' })}
        </button>
      </div>

      {activeTab === 'details' ? detailsContent : renderInsightsTab()}
    </>
  );
}
